/**
 * User accounts (spec §8.26): create, edit, set a password, make or remove an administrator,
 * deactivate and reactivate. The server's guards (self, last administrator, Keycloak accounts) are
 * shown as disabled actions with their reason, and enforced there again.
 */
import { clsx } from "clsx";
import { MoreHorizontal, UserPlus } from "lucide-react";
import { useState } from "react";
import { useMe } from "../api/hooks";
import { useI18n, useT } from "../i18n";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { usePopover } from "../ui/usePopover";
import { ServerRefusal, useCreateUser, useRevokeUserTokens, useServerUsers, useUpdateUser, type FieldError, type ServerUser } from "./api";
import { ERROR, REFUSED } from "./labels";

type Dialog = { kind: "new" } | { kind: "edit"; user: ServerUser } | { kind: "password"; user: ServerUser };

export function UsersTab() {
  const t = useT();
  const users = useServerUsers();
  const me = useMe();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [error, setError] = useState<string | null>(null);
  const activeAdmins = (users.data ?? []).filter((u) => u.isAdmin && u.isActive).length;

  return (
    <section className="kss-surface p-4" data-testid="server-users-tab">
      <div className="mb-3 flex items-center gap-3">
        <div className="grow">
          <h2 className="text-[17px] font-semibold">{t("server.users")}</h2>
          <p className="text-[13px] text-neutral-500">{t("server.users.sub")}</p>
        </div>
        <Button onClick={() => setDialog({ kind: "new" })} data-testid="server-user-new">
          <UserPlus size={15} aria-hidden="true" />
          {t("server.users.new")}
        </Button>
      </div>
      {error && (
        <p className="mb-3 rounded-sm bg-err-tint px-3 py-2 text-sm text-err" role="alert" data-testid="server-users-error">
          {error}
        </p>
      )}
      {users.isError && <p className="text-sm text-neutral-500">{t("server.needServer")}</p>}
      <div>
        <table className="w-full text-sm" data-testid="server-user-list">
          <thead className="text-left text-xs text-neutral-500">
            <tr>
              <th className="py-1.5 pr-3 font-medium">{t("server.users.col.user")}</th>
              <th className="py-1.5 pr-3 font-medium">{t("server.users.col.signIn")}</th>
              <th className="hidden py-1.5 pr-3 font-medium sm:table-cell">{t("server.users.col.lastLogin")}</th>
              <th className="hidden py-1.5 pr-3 text-right font-medium sm:table-cell">{t("server.users.col.workspaces")}</th>
              <th className="py-1.5" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-200 dark:divide-neutral-800">
            {users.data?.map((u) => (
              <UserRow key={u.id} user={u} self={u.id === me.data?.id} lastAdmin={u.isAdmin && u.isActive && activeAdmins <= 1} onDialog={setDialog} onError={setError} />
            ))}
          </tbody>
        </table>
      </div>
      {dialog?.kind === "new" && <NewUserDialog onClose={() => setDialog(null)} />}
      {dialog?.kind === "edit" && <EditDialog user={dialog.user} onClose={() => setDialog(null)} />}
      {dialog?.kind === "password" && <PasswordDialog user={dialog.user} onClose={() => setDialog(null)} />}
    </section>
  );
}

function UserRow({ user, self, lastAdmin, onDialog, onError }: { user: ServerUser; self: boolean; lastAdmin: boolean; onDialog(d: Dialog): void; onError(m: string | null): void }) {
  const t = useT();
  const { language } = useI18n();
  const update = useUpdateUser();
  const revokeTokens = useRevokeUserTokens();
  const { open: menuOpen, toggle: menuToggle, close: menuClose, buttonRef, panelRef } = usePopover();
  const act = (patch: { isAdmin?: boolean; isActive?: boolean }) => {
    onError(null);
    menuClose();
    update.mutate({ id: user.id, ...patch }, { onError: (e) => onError(e instanceof ServerRefusal && e.code ? t(REFUSED[e.code] ?? e.code) : String(e)) });
  };
  const guard = self ? t("server.users.guard.self") : lastAdmin ? t("server.users.guard.lastAdmin") : null;
  return (
    <tr className={clsx(!user.isActive && "text-neutral-400")} data-testid="server-user-row" data-username={user.username} data-active={user.isActive} data-admin={user.isAdmin}>
      <td className="py-2 pr-3">
        <div className="font-medium">
          {user.displayName || user.username}
          {self && <span className="ml-1 text-xs font-normal text-neutral-500">{t("settings.members.you")}</span>}
        </div>
        <div className="text-xs text-neutral-500">
          {user.username}
          {user.email && ` · ${user.email}`}
        </div>

        <div className="mt-0.5 flex gap-1">
          {user.isAdmin && <span className="kss-pill border-accent text-accent">{t("server.users.admin")}</span>}
          {!user.isActive && <span className="kss-pill border-neutral-300 text-neutral-500 dark:border-neutral-700">{t("server.users.inactive")}</span>}
        </div>
      </td>
      <td className="py-2 pr-3">
        {user.oidc ? t("server.users.signIn.oidc") : t("server.users.signIn.password")}
        {user.oidc && user.keycloakUsername && (
          <span className="block text-xs text-neutral-500" data-testid="server-user-keycloak">
            {user.keycloakUsername}
          </span>
        )}
        {user.mcpTokens > 0 && (
          <span className="block text-xs text-neutral-500" data-testid="server-user-mcp-tokens">
            {t("server.users.mcpTokens", { count: user.mcpTokens })}
          </span>
        )}
      </td>
      <td className="hidden py-2 pr-3 text-neutral-500 sm:table-cell">{user.lastLogin ? new Date(user.lastLogin).toLocaleDateString(language) : t("server.users.never")}</td>
      <td className="hidden py-2 pr-3 text-right tabular-nums sm:table-cell">{user.workspaces}</td>
      <td className="relative py-2 text-right">
        <button
          ref={buttonRef}
          className="rounded p-1 text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
          aria-label={t("server.users.menu", { user: user.username })}
          aria-expanded={menuOpen}
          data-testid="server-user-menu-button"
          onClick={menuToggle}
        >
          <MoreHorizontal size={16} />
        </button>
        {menuOpen && (
          <div ref={panelRef} className="absolute right-0 top-full z-20 w-60 rounded-md border border-neutral-200 bg-[var(--surface)] py-1 text-left shadow-[var(--shadow-lg)] dark:border-neutral-700" role="menu" data-testid="server-user-menu">
            <Item onClick={() => (menuClose(), onDialog({ kind: "edit", user }))} testId="server-user-edit">
              {t("server.users.edit")}
            </Item>
            <Item onClick={() => (menuClose(), onDialog({ kind: "password", user }))} disabled={user.oidc ? t("server.users.guard.oidc") : null} testId="server-user-password">
              {t("server.users.password")}
            </Item>
            {user.isAdmin ? (
              <Item onClick={() => act({ isAdmin: false })} disabled={guard} testId="server-user-admin">
                {t("server.users.removeAdmin")}
              </Item>
            ) : (
              <Item onClick={() => act({ isAdmin: true })} testId="server-user-admin">
                {t("server.users.makeAdmin")}
              </Item>
            )}
            {user.isActive ? (
              <Item onClick={() => act({ isActive: false })} disabled={self ? t("server.users.guard.self") : user.isAdmin && lastAdmin ? t("server.users.guard.lastAdmin") : null} danger testId="server-user-active">
                {t("server.users.deactivate")}
              </Item>
            ) : (
              <Item onClick={() => act({ isActive: true })} testId="server-user-active">
                {t("server.users.reactivate")}
              </Item>
            )}
            {user.mcpTokens > 0 && (
              <Item
                onClick={() => {
                  menuClose();
                  revokeTokens.mutate(user.id);
                }}
                danger
                testId="server-user-revoke-tokens"
              >
                {t("server.users.revokeTokens")}
              </Item>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

function Item({ children, onClick, disabled, danger, testId }: { children: React.ReactNode; onClick(): void; disabled?: string | null; danger?: boolean; testId: string }) {
  return (
    <button
      type="button"
      role="menuitem"
      className={clsx(
        "block w-full px-3 py-1.5 text-left text-sm hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent dark:hover:bg-neutral-800",
        danger && "text-err",
      )}
      disabled={!!disabled}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
      {disabled && <span className="block text-xs text-neutral-500">{disabled}</span>}
    </button>
  );
}

function Modal({ title, onClose, children, testId }: { title: string; onClose(): void; children: React.ReactNode; testId: string }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-[rgba(28,27,26,0.36)] p-8" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-lg border border-neutral-200 bg-[var(--surface)] p-5 text-sm shadow-[var(--shadow-lg)] dark:border-neutral-700"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        role="dialog"
        aria-label={title}
        data-testid={testId}
      >
        <h2 className="mb-3 text-[17px] font-semibold">{title}</h2>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children, error }: { label: string; children: React.ReactNode; error?: string | null }) {
  return (
    <label className="mb-3 block">
      <span className="mb-1 block text-xs font-medium text-neutral-500">{label}</span>
      {children}
      {error && (
        <span className="mt-1 block text-xs text-err" role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

function useFieldErrors() {
  const t = useT();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fail = (e: unknown) => {
    if (e instanceof ServerRefusal) {
      if (e.code) setErrors({ _: t(REFUSED[e.code] ?? e.code) });
      else setErrors(Object.fromEntries(Object.entries(e.errors).map(([k, v]) => [k, describe(t, v)])));
    } else setErrors({ _: String(e) });
  };
  return { errors, fail, clear: () => setErrors({}) };
}

function describe(t: ReturnType<typeof useT>, e: FieldError): string {
  if (e.code === "password" && Array.isArray(e.messages)) return e.messages.join(" ");
  return t(ERROR[e.code] ?? e.code);
}

function Actions({ onClose, busy, submit, testId }: { onClose(): void; busy: boolean; submit: string; testId: string }) {
  const t = useT();
  return (
    <div className="mt-4 flex justify-end gap-2">
      <Button type="button" variant="ghost" onClick={onClose}>
        {t("common.cancel")}
      </Button>
      <Button type="submit" disabled={busy} data-testid={testId}>
        {submit}
      </Button>
    </div>
  );
}

function NewUserDialog({ onClose }: { onClose(): void }) {
  const t = useT();
  const create = useCreateUser();
  const { errors, fail, clear } = useFieldErrors();
  const [f, setF] = useState({ username: "", displayName: "", email: "", password: "", isAdmin: false });
  return (
    <Modal title={t("server.users.new")} onClose={onClose} testId="server-user-dialog">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          clear();
          create.mutate(f, { onSuccess: onClose, onError: fail });
        }}
      >
        <Field label={t("auth.username")} error={errors.username}>
          <Input autoFocus required value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} data-testid="server-user-username" />
        </Field>
        <Field label={t("server.users.displayName")}>
          <Input value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} data-testid="server-user-display-name" />
        </Field>
        <Field label={t("server.users.email")}>
          <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="server-user-email" />
        </Field>
        <Field label={t("auth.password")} error={errors.password}>
          <Input type="password" autoComplete="new-password" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="server-user-new-password" />
        </Field>
        <label className="flex items-center gap-2">
          <input type="checkbox" className="h-4 w-4 accent-[var(--color-accent)]" checked={f.isAdmin} onChange={(e) => setF({ ...f, isAdmin: e.target.checked })} data-testid="server-user-is-admin" />
          {t("server.users.isAdmin")}
        </label>
        {errors._ && (
          <p className="mt-3 text-sm text-err" role="alert">
            {errors._}
          </p>
        )}
        <Actions onClose={onClose} busy={create.isPending} submit={t("server.users.create")} testId="server-user-submit" />
      </form>
    </Modal>
  );
}

function EditDialog({ user, onClose }: { user: ServerUser; onClose(): void }) {
  const t = useT();
  const update = useUpdateUser();
  const { errors, fail, clear } = useFieldErrors();
  const [f, setF] = useState({ displayName: user.displayName, email: user.email });
  return (
    <Modal title={t("server.users.editTitle", { user: user.username })} onClose={onClose} testId="server-user-dialog">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          clear();
          update.mutate({ id: user.id, ...f }, { onSuccess: onClose, onError: fail });
        }}
      >
        <Field label={t("server.users.displayName")}>
          <Input autoFocus value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} data-testid="server-user-display-name" />
        </Field>
        <Field label={t("server.users.email")}>
          <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="server-user-email" />
        </Field>
        {errors._ && <p className="text-sm text-err">{errors._}</p>}
        <Actions onClose={onClose} busy={update.isPending} submit={t("server.save")} testId="server-user-submit" />
      </form>
    </Modal>
  );
}

function PasswordDialog({ user, onClose }: { user: ServerUser; onClose(): void }) {
  const t = useT();
  const update = useUpdateUser();
  const { errors, fail, clear } = useFieldErrors();
  const [password, setPassword] = useState("");
  return (
    <Modal title={t("server.users.passwordTitle", { user: user.username })} onClose={onClose} testId="server-user-dialog">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          clear();
          update.mutate({ id: user.id, password }, { onSuccess: onClose, onError: fail });
        }}
      >
        <Field label={t("server.users.newPassword")} error={errors.password}>
          <Input autoFocus type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="server-user-new-password" />
        </Field>
        {errors._ && <p className="text-sm text-err">{errors._}</p>}
        <Actions onClose={onClose} busy={update.isPending} submit={t("server.users.setPassword")} testId="server-user-submit" />
      </form>
    </Modal>
  );
}
